import React, { useState, useEffect } from "react";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import "./Modal.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://agent-book-crm-backend.onrender.com";

const formConfig = {
  MAPD: [
    { name: "carrierId", label: "Carrier", type: "readonly" },
    { name: "planName", label: "Plan Name", type: "text", required: true },
    { name: "planId", label: "Plan ID (format AXXXX-XXX-X)", type: "text", pattern: "^[A-Za-z]{1}\\d{4}-\\d{3}-\\d{1}$", required: true },
    { name: "planType", label: "Plan Type", type: "select", options: ["PPO", "HMO", "HMO-POS", "DSNP", "CSNP", "PFFS"], required: true }
  ],
  "Individual Health": [
    { name: "carrierId", label: "Carrier", type: "readonly" },
    { name: "planName", label: "Plan Name", type: "text", required: true },
    { name: "planType", label: "Plan Type", type: "select", options: ["Marketplace Health", "Short-Term Health"], required: true }
  ],
  Life: [
    { name: "carrierId", label: "Carrier", type: "readonly" },
    { name: "planName", label: "Plan Name", type: "text", required: true },
    { name: "planType", label: "Plan Type", type: "select", options: ["Term Life", "Whole Life", "Accidental Death"], required: true }
  ],
  Dental: [
    { name: "carrierId", label: "Carrier", type: "readonly" },
    { name: "planName", label: "Plan Name", type: "text", required: true },
    { name: "planType", label: "Plan Type", type: "select", options: ["Dental Only", "Dental/Vision/Hearing"], required: true },
    { name: "benefitAmount", label: "Benefit Amount", type: "checkboxGroup", options: ["$1000", "$1500", "$2000", "$2500", "$3000", "$5000"], required: true }
  ],
  "Medicare Supplement": [
    { name: "carrierId", label: "Carrier", type: "readonly" },
    { name: "planName", label: "Plan Name", type: "checkboxGroup", options: ["Plan A", "Plan F", "Plan G", "Plan HG", "Plan N"], required: true }
  ],
  PDP: [
    { name: "carrierId", label: "Carrier", type: "readonly" },
    { name: "planName", label: "Plan Name", type: "text", required: true },
    { name: "planId", label: "Plan ID (format AXXXX-XXX-X)", type: "text", pattern: "^[A-Za-z]{1}\\d{4}-\\d{3}-\\d{1}$", required: true }
  ]
};

const EditProductModal = ({ product, coverageTypes, onClose, onEdit, onDelete }) => {
  const [formData, setFormData] = useState({});
  const [selectedTypeName, setSelectedTypeName] = useState("");
  const [carriers, setCarriers] = useState([]);
  const [error, setError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const fetchCarriers = async () => {
      const token = localStorage.getItem("token");

      try {
        const res = await fetch(`${API_BASE_URL}/api/carriers`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const data = await res.json();
        setCarriers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching carriers:", err);
        setCarriers([]);
      }
    };

    fetchCarriers();
  }, []);

  useEffect(() => {
    let typeName = product.coverageTypeName;

    if (!typeName && coverageTypes.length > 0) {
      const ct = coverageTypes.find(
        (coverageType) =>
          parseInt(coverageType.id, 10) === parseInt(product.coverage_type_id, 10)
      );

      typeName = ct?.name;
    }

    setSelectedTypeName(typeName || "");

    setFormData({
      carrierId: product.carrier_id || "",
      planName: product.plan_name || "",
      planId: product.plan_id || "",
      planType: product.plan_type || "",
      benefitAmount: product.benefit_amount
        ? String(product.benefit_amount).split(",")
        : []
    });
  }, [coverageTypes, product]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => {
      if (type === "checkbox") {
        const arr = Array.isArray(prev[name]) ? prev[name] : [];

        return {
          ...prev,
          [name]: checked
            ? [...arr, value]
            : arr.filter((v) => v !== value)
        };
      }

      return { ...prev, [name]: value };
    });
  };

  const normalizeSubmitValue = (fieldName, value) => {
    if (fieldName === "benefitAmount" && Array.isArray(value)) {
      return value.join(",");
    }

    if (fieldName === "planName" && Array.isArray(value)) {
      return value.join(",");
    }

    return value;
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError("");

    const cfg = formConfig[selectedTypeName] || [];

    for (const field of cfg) {
      const val = formData[field.name];

      if (field.required && (!val || (Array.isArray(val) && val.length === 0))) {
        setError(`Field "${field.label}" is required.`);
        return;
      }

      if (field.pattern && val && !(new RegExp(field.pattern).test(val))) {
        setError(`Field "${field.label}" does not match the required format.`);
        return;
      }
    }

    try {
      await onEdit({
        ...formData,
        carrierId: parseInt(formData.carrierId, 10),
        planName: normalizeSubmitValue("planName", formData.planName),
        benefitAmount: normalizeSubmitValue("benefitAmount", formData.benefitAmount),
        coverageTypeId: product.coverage_type_id
      });

      onClose();
    } catch (err) {
      console.error("Error updating product:", err);
      setError("Failed to update product.");
    }
  };

  const carrierName =
    product.carrier ||
    product.carrierName ||
    carriers.find((carrier) => carrier.id === parseInt(formData.carrierId, 10))?.name ||
    "";

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Edit Product — {selectedTypeName}</h2>

        {error && <p className="error">{error}</p>}

        <form onSubmit={handleUpdate}>
          {formConfig[selectedTypeName]?.map((field) => (
            <div className="form-group" key={field.name}>
              <label>{field.label}</label>

              {field.type === "readonly" ? (
                <div className="readonly-display">
                  <strong>{carrierName}</strong>
                </div>
              ) : field.type === "select" ? (
                <select
                  name={field.name}
                  value={formData[field.name] || ""}
                  onChange={handleChange}
                  required={field.required}
                >
                  <option value="">Select {field.label}</option>

                  {field.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : field.type === "checkboxGroup" ? (
                <div className="checkbox-group">
                  {field.options.map((option) => (
                    <label key={option} className="checkbox-option">
                      <input
                        type="checkbox"
                        name={field.name}
                        value={option}
                        checked={(formData[field.name] || []).includes(option)}
                        onChange={handleChange}
                      />
                      {option}
                    </label>
                  ))}
                </div>
              ) : (
                <input
                  type="text"
                  name={field.name}
                  value={formData[field.name] || ""}
                  onChange={handleChange}
                  required={field.required}
                  pattern={field.pattern || undefined}
                />
              )}
            </div>
          ))}

          {!formConfig[selectedTypeName] && (
            <p className="error">
              Products are not configured for this coverage type yet.
            </p>
          )}

          <div className="modal-actions">
            <button
              type="submit"
              className="btn-save"
              disabled={!formConfig[selectedTypeName]}
            >
              Update
            </button>

            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>

            <button
              type="button"
              className="link-delete"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={!formConfig[selectedTypeName]}
            >
              Delete Product
            </button>
          </div>
        </form>

        {showDeleteConfirm && (
          <DeleteConfirmationModal
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={() => {
              onDelete(product.id, product.coverage_type_id);
              onClose();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default EditProductModal;